<template>
  <main class="login-page">
    <div class="login-shell">
      <DesktopInstall class="login-install" />
      <section class="login-panel">
        <LanguageSwitcher class="language-switcher--panel" />
        <div class="brand-lockup">
          <img class="brand-mark" :src="platformLogo" :alt="language.t('auth.iconAlt', { name: platformName })" @error="handleBrandLogoError">
          <div>
            <h1>{{ platformName }}</h1>
          </div>
        </div>

        <form v-if="view === 'login'" class="login-form" @submit.prevent="submit" @input="handleLoginInput" @focusin="syncLoginCredentials" @change="syncLoginCredentials" @animationstart="syncLoginCredentials">
        <div class="login-tabs">
          <button type="button" :class="{ active: mode === 'password' }" @click="setLoginMode('password')">{{ language.t('auth.passwordLogin') }}</button>
          <button type="button" :class="{ active: mode === 'code' }" @click="setLoginMode('code')">{{ language.t('auth.codeLogin') }}</button>
        </div>

        <label>
          <span>{{ mode === 'password' ? language.t('registration.identity') : language.t('auth.phone') }}</span>
          <div class="phone-row">
            <div class="country-code-field">
              <span>+</span>
              <input v-model.trim="loginCountryCode" :aria-label="language.t('auth.phoneCountryCode')" autocomplete="tel-country-code" inputmode="numeric" maxlength="6" @input="loginCountryCode = loginCountryCode.replace(/\D/g, '')">
            </div>
            <input ref="loginIdentityInput" v-model.trim="mobile" autocomplete="username" :inputmode="mode === 'password' ? 'text' : 'tel'" :placeholder="mode === 'password' ? language.t('registration.identityPlaceholder') : language.t('auth.phonePlaceholder')">
          </div>
        </label>
        <label v-if="mode === 'password'">
          <span>{{ language.t('auth.password') }}</span>
          <input ref="loginPasswordInput" v-model="password" autocomplete="current-password" type="password" :placeholder="language.t('auth.passwordPlaceholder')">
        </label>
        <label v-else>
          <span>{{ language.t('auth.code') }}</span>
          <div class="code-row">
            <input v-model.trim="code" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.localCodePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || mobile.length < 5" @click="requestCode">
              {{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}
            </button>
          </div>
        </label>
        <button class="primary-action" :disabled="session.connecting">
          {{ session.connecting ? language.t('auth.loggingIn') : language.t('auth.login') }}
        </button>
        <p v-if="loginFormError || sessionError" class="form-error">
          {{ loginFormError || language.error(sessionError) }}
          <small v-if="session.errorCode" class="form-error-code">{{ language.t('error.reference', { code: session.errorCode }) }}</small>
        </p>
        <p v-if="info" class="form-info">{{ language.t(info) }}</p>
        </form>

      <form v-else-if="view === 'register'" class="login-form" @submit.prevent="submitRegister">
        <p v-if="registrationLoading" class="form-info">{{ language.t('registration.loading') }}</p>
        <p v-else-if="!visibleRegisterMethods.length" class="form-info">{{ language.t('registration.closed') }}</p>
        <div v-if="!registrationLoading && visibleRegisterMethods.length > 1 && !registerType" class="registration-methods">
          <button v-for="item in visibleRegisterMethods" :key="item.key" class="ghost-action" type="button" @click="selectRegisterMethod(item.key)">{{ item.label }}</button>
        </div>
        <template v-if="!registrationLoading && registerType && visibleRegisterMethods.length">
          <h2 class="registration-heading">{{ visibleRegisterMethods.find(item => item.key === registerType)?.label }}</h2>

        <label v-if="registerType === 'phone'">
          <span class="label-row">
            <span>{{ language.t('auth.phone') }}</span>
            <span v-if="registerPhoneError" class="field-error-badge">{{ language.error(registerPhoneError) }}</span>
          </span>
          <div class="phone-row">
            <div class="country-code-field">
              <span>+</span>
              <input
                v-model.trim="registerForm.phoneCountryCode"
                :aria-label="language.t('auth.phoneCountryCode')"
                autocomplete="tel-country-code"
                inputmode="numeric"
                maxlength="6"
                @input="normalizeRegisterCountryCode"
              >
            </div>
            <input
              v-model.trim="registerForm.phone"
              :class="{ 'field-input-error': registerPhoneError }"
              autocomplete="tel"
              inputmode="tel"
              :placeholder="language.t('auth.phonePlaceholder')"
              @input="clearRegisterPhoneError"
            >
          </div>
        </label>
        <label v-if="registerType === 'phone'">
          <span>{{ language.t('auth.code') }}</span>
          <div v-if="mobileRealVerify" class="code-row">
            <input v-model.trim="registerForm.code" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.smsCodePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || registerForm.phone.length < 5" @click="requestRegisterCode">
              {{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}
            </button>
          </div>
          <div v-else class="captcha-row">
            <input
              v-model.trim="registerForm.code"
              :class="phoneCaptchaClass"
              autocomplete="off"
              inputmode="text"
              autocapitalize="none"
              spellcheck="false"
              maxlength="6"
              :placeholder="language.t('auth.codePlaceholder')"
              @input="normalizePhoneCaptchaInput"
            >
            <button v-if="!phoneCaptchaImage" type="button" class="code-action" :disabled="sendingCode || registerForm.phone.length < 5" @click="showPhoneCaptcha">
              {{ language.t('auth.sendCode') }}
            </button>
            <button v-else type="button" class="captcha-button" :title="language.t('auth.refreshCaptcha')" :disabled="sendingCode || registerForm.phone.length < 5" @click="showPhoneCaptcha">
              <img :src="phoneCaptchaImage" :alt="language.t('auth.captchaAlt')">
            </button>
          </div>
        </label>

        <label v-if="emailRegisterEnabled && registerType === 'email'">
          <span>{{ language.t('auth.email') }}</span>
          <input v-model.trim="registerForm.email" autocomplete="email" inputmode="email" :placeholder="language.t('auth.emailPlaceholder')">
        </label>
        <label v-if="emailRegisterEnabled && registerType === 'email'">
          <span>{{ language.t('auth.emailCode') }}</span>
          <div class="code-row">
            <input v-model.trim="registerForm.emailCode" autocomplete="one-time-code" inputmode="numeric" :placeholder="language.t('auth.codePlaceholder')">
            <button type="button" class="code-action" :disabled="sendingCode || !registerForm.email" @click="requestEmailCode">{{ sendingCode ? language.t('auth.sending') : language.t('auth.sendCode') }}</button>
          </div>
        </label>

        <label v-if="registerType === 'username'">
          <span>{{ language.t('auth.username') }}</span>
          <input v-model.trim="registerForm.username" autocomplete="username" maxlength="128" :aria-invalid="registerForm.username && !usernameValid ? 'true' : 'false'" aria-describedby="username-rules" :placeholder="language.t('auth.usernamePlaceholder')">
          <span v-if="registerForm.username && !usernameValid" class="form-error">{{ language.t('registration.usernameInvalid') }}</span>
          <small id="username-rules">{{ language.t('registration.usernameRules') }}</small>
        </label>
        <label v-if="registerType === 'username'">
          <span>{{ language.t('auth.code') }}</span>
          <div class="captcha-row">
            <input
              v-model.trim="registerForm.usernameCode"
              :class="usernameCaptchaClass"
              autocomplete="off"
              inputmode="text"
              autocapitalize="none"
              spellcheck="false"
              maxlength="6"
              :placeholder="language.t('auth.codePlaceholder')"
              @input="normalizeUsernameCaptchaInput"
            >
            <button v-if="!usernameCaptchaImage" type="button" class="code-action" :disabled="sendingCode || !usernameValid" @click="showUsernameCaptcha">
              {{ language.t('auth.showCaptcha') }}
            </button>
            <button v-else type="button" class="captcha-button" :title="language.t('auth.refreshCaptcha')" :disabled="sendingCode || !usernameValid" @click="showUsernameCaptcha">
              <img :src="usernameCaptchaImage" :alt="language.t('auth.captchaAlt')">
            </button>
          </div>
        </label>

        <label v-if="visibleRegisterMethods.length">
          <span>{{ language.t('auth.password') }}</span>
          <input v-model="registerForm.password" autocomplete="new-password" type="password" minlength="6" maxlength="128" :placeholder="language.t('registration.passwordHint')">
        </label>
        <label v-if="visibleRegisterMethods.length">
          <span>{{ language.t('auth.confirmPassword') }}</span>
          <input v-model="registerForm.confirmPassword" autocomplete="new-password" type="password" :placeholder="language.t('auth.confirmPasswordPlaceholder')">
        </label>

        <label class="referrer-field"><span>{{language.t('registration.referrerLabel')}}</span><div class="referrer-row"><input ref="referrerInputElement" v-model.trim="registerForm.referrer" maxlength="128" :disabled="registering" :class="{'referrer-not-found':referrerMissing}" :aria-invalid="referrerMissing?'true':'false'" aria-describedby="referrer-feedback" :placeholder="language.t('registration.referrerPlaceholder')"></div><small id="referrer-feedback" :class="{'form-error':referrerErrorKey}">{{referrerSearching?language.t('registration.referrerSearching'):referrerErrorKey?language.t(referrerErrorKey):selectedReferrer?language.t('registration.referrerSelected',{name:selectedReferrer.nickname||selectedReferrer.account,id:selectedReferrer.userID}):''}}</small></label>

        <button class="primary-action" :disabled="registering || referrerSearching || sendingCode || registrationLoading || !canRegisterSubmit">
          {{ registering ? language.t('auth.registering') : language.t('auth.register') }}
        </button>
        </template>
        <button v-if="registerType && visibleRegisterMethods.length > 1" class="ghost-action" type="button" @click="selectRegisterMethod('')">{{ language.t('registration.backMethods') }}</button>
        <p v-if="sessionError" class="form-error">{{ language.error(sessionError) }}</p>
        <p v-if="info" class="form-info">{{ language.t(info) }}</p>
      </form>

      <form v-else class="login-form" @submit.prevent="submitRegisterPassword">
        <label>
          <span>{{ language.t('auth.phone') }}</span>
          <input :value="pendingRegistration.mobile" autocomplete="username" readonly>
        </label>
        <label>
          <span>{{ language.t('auth.loginPassword') }}</span>
          <input v-model="registerPasswordForm.password" autocomplete="new-password" type="password" :placeholder="language.t('auth.loginPasswordPlaceholder')">
        </label>
        <label>
          <span>{{ language.t('auth.confirmPassword') }}</span>
          <input v-model="registerPasswordForm.confirmPassword" autocomplete="new-password" type="password" :placeholder="language.t('auth.confirmPasswordPlaceholder')">
        </label>
        <button class="primary-action" :disabled="session.connecting || settingRegisterPassword || !canSetRegisterPassword">
          {{ settingRegisterPassword || session.connecting ? language.t('common.saving') : language.t('auth.saveAndEnter') }}
        </button>
        <p v-if="sessionError" class="form-error">{{ language.error(sessionError) }}</p>
        <p v-if="info" class="form-info">{{ language.t(info) }}</p>
      </form>

      <button v-if="view === 'login'" class="ghost-action" type="button" @click="openRegister">
        {{ language.t('auth.newUserRegister') }}
      </button>
      <button v-else-if="view === 'register'" class="ghost-action" type="button" @click="backToLogin">
        {{ language.t('auth.backToLogin') }}
      </button>
      </section>
    </div>
    <div v-if="referrerDialog" class="referrer-overlay" @click.self="closeReferrerDialog"><section ref="referrerDialogElement" class="referrer-dialog" role="dialog" aria-modal="true" aria-labelledby="referrer-dialog-title" @keydown="handleReferrerDialogKey"><header><h2 id="referrer-dialog-title">{{language.t('registration.referrerSelectTitle')}}</h2><button type="button" :aria-label="language.t('registration.referrerCancel')" @click="closeReferrerDialog">×</button></header><p>{{language.t('registration.referrerMultiple')}}</p><div class="referrer-options"><button v-for="person in referrerMatches" :key="person.userID" type="button" @click="chooseReferrer(person)"><strong>{{person.nickname||person.account||person.userID}}</strong><span>ID: {{person.userID}}</span><small v-if="person.account">{{person.account}}</small><small v-if="person.phoneHint">{{person.phoneHint}}</small></button></div><button class="ghost-action" type="button" @click="closeReferrerDialog">{{language.t('registration.referrerCancel')}}</button></section></div>
  </main>
</template>

<script setup>
import { validateRegistrationUsername } from '@/services/registrationUsername';
import { registrationErrorKey } from '@/services/registration';
import { projectBrand, loadProjectBrand } from '@/services/projectBrand';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { appConfig } from '@/config';
import DesktopInstall from '@/components/DesktopInstall.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { useSessionStore } from '@/stores/session';
import { useLanguageStore } from '@/stores/language';
import { registrationRequest } from '@/services/registration';
import { imClient } from '@/services/imClient';

const session = useSessionStore();
const language = useLanguageStore();
const view = ref('login');
const mode = ref('password');
const mobile = ref('');
const loginFormError = ref('');
const loginIdentityInput = ref(null);
const loginPasswordInput = ref(null);
const loginAutofillSyncTimers = [];
const languageCountryCodes = { zh_hans: '86', zh_hant: '886', en: '1', km: '855', lo: '856', th: '66', vi: '84' };
const defaultCountryCode = computed(() => languageCountryCodes[language.current] || '86');
const loginCountryCode = ref(defaultCountryCode.value);
const loginPhone = () => `+${loginCountryCode.value} ${mobile.value.replace(/\D/g, '')}`;
const password = ref('');
const code = ref('666666');
const sendingCode = ref(false);
const info = ref('');
const pendingRegistrationStorageKey = 'qmt-web-im-pending-registration';
const defaultPlatformLogo = '/img/milian-logo.png';
const platformName = computed(() => projectBrand.name);
const platformLogo = computed(() => projectBrand.icon_url);
const registerType = ref('');
const emailRegisterEnabled = true;
const registering = ref(false);
const referrerSearching=ref(false),referrerMissing=ref(false),referrerErrorKey=ref(''),selectedReferrer=ref(null),referrerMatches=ref([]),referrerDialog=ref(false),referrerInputElement=ref(null),referrerDialogElement=ref(null);
let referrerLookupEpoch=0,registerAfterReferrerChoice=false;
const registrationLoading = ref(true);
const emailChallenge = ref('');
const registerMethods = ref({ phone: false, email: false, username: false });
const mobileRealVerify = ref(false);
const registerPhoneError = ref('');
const phoneCaptcha = ref('');
const phoneCaptchaImage = ref('');
const usernameCaptcha = ref('');
const usernameCaptchaImage = ref('');
const settingRegisterPassword = ref(false);
const pendingRegistration = reactive({
  mobile: '',
  result: null,
  resetCode: ''
});
const registerForm = reactive({
  referrer: '',
  phoneCountryCode: defaultCountryCode.value,
  phone: '',
  code: '',
  email: '',
  emailCode: '',
  username: '',
  usernameCode: '',
  password: '',
  confirmPassword: ''
});
const registerPasswordForm = reactive({
  password: '',
  confirmPassword: ''
});
const registerMethodOptions = computed(() => [
  { key: 'phone', label: language.t('auth.phoneRegister') },
  { key: 'email', label: language.t('auth.emailRegister') },
  { key: 'username', label: language.t('auth.usernameRegister') }
]);
const sessionError = computed(() => {
  return session.errorKey ? language.t(session.errorKey) : session.error;
});
const visibleRegisterMethods = computed(() => registerMethodOptions.value.filter(item => registerMethods.value[item.key]));
const usernameValid = computed(() => validateRegistrationUsername(registerForm.username));
const canRegisterSubmit = computed(() => {
  if (!visibleRegisterMethods.value.length || registerForm.password.length < 6 || registerForm.password !== registerForm.confirmPassword) return false;
  if (registerType.value === 'phone') return registerForm.phone.length >= 5 && (mobileRealVerify.value ? phoneCaptcha.value && /^\d{6}$/.test(registerForm.code) : phoneCaptchaMatched.value);
  if (registerType.value === 'email') return registerForm.email.includes('@') && emailChallenge.value && /^\d{6}$/.test(registerForm.emailCode);
  return usernameValid.value && usernameCaptchaMatched.value;
});
const canSetRegisterPassword = computed(() => {
  return pendingRegistration.mobile
    && pendingRegistration.resetCode
    && registerPasswordForm.password.length >= 1
    && registerPasswordForm.password === registerPasswordForm.confirmPassword;
});
const phoneCaptchaStatus = computed(() => '');
const phoneCaptchaMatched = computed(() => Boolean(phoneCaptcha.value) && /^[ac-hjkmnpr-z2-7]{6}$/.test(registerForm.code));
const phoneCaptchaClass = computed(() => getCaptchaClass(phoneCaptchaStatus.value));
const usernameCaptchaStatus = computed(() => '');
const usernameCaptchaMatched = computed(() => Boolean(usernameCaptcha.value) && /^[ac-hjkmnpr-z2-7]{6}$/.test(registerForm.usernameCode));
const usernameCaptchaClass = computed(() => getCaptchaClass(usernameCaptchaStatus.value));
watch(() => language.current, () => {
  loginCountryCode.value = defaultCountryCode.value;
  registerForm.phoneCountryCode = defaultCountryCode.value;
});

watch(visibleRegisterMethods, methods => {
  if (!methods.some(item => item.key === registerType.value)) {
    registerType.value = methods.length === 1 ? methods[0].key : '';
  }
});
watch(() => [registerForm.phone, registerForm.phoneCountryCode], () => { phoneCaptcha.value='';phoneCaptchaImage.value='';registerForm.code=''; });
watch(() => registerForm.email, () => { emailChallenge.value='';registerForm.emailCode=''; });
watch(() => registerForm.referrer,()=>{referrerLookupEpoch++;selectedReferrer.value=null;referrerMissing.value=false;referrerErrorKey.value='';referrerDialog.value=false;registerAfterReferrerChoice=false;});
watch(() => registerForm.username, () => { usernameCaptcha.value='';usernameCaptchaImage.value='';registerForm.usernameCode=''; });
watch(() => session.error, value => {
  if (value) {
    session.errorKey = '';
  }
});

function syncLoginCredentials() {
  const identity = loginIdentityInput.value?.value;
  const passwordValue = loginPasswordInput.value?.value;
  if (typeof identity === 'string' && identity !== mobile.value) mobile.value = identity.trim();
  if (typeof passwordValue === 'string' && passwordValue !== password.value) password.value = passwordValue;
}

function handleLoginInput() {
  loginFormError.value = '';
  syncLoginCredentials();
}

onMounted(() => {
  loadPlatformConfig();
  loadRegisterConfig();
  restorePendingRegistration();
  nextTick(syncLoginCredentials);
  for (const delay of [150, 500, 1200, 2500, 5000]) {
    loginAutofillSyncTimers.push(setTimeout(syncLoginCredentials, delay));
  }
});

onBeforeUnmount(() => loginAutofillSyncTimers.forEach(timer => clearTimeout(timer)));

async function requestCode() {
  info.value = '';
  sendingCode.value = true;
  try {
    await imClient.requestAuthCode(loginPhone());
    info.value = '验证码已发送；本地测试也可以直接使用 666666';
  } catch (error) {
    session.error = error.message || '验证码发送失败';
  } finally {
    sendingCode.value = false;
  }
}

function registrationRecipient() {
  if (registerType.value === 'phone') return `+${registerForm.phoneCountryCode || defaultCountryCode.value}${registerForm.phone.replace(/\s/g, '')}`;
  return registerType.value === 'email' ? registerForm.email : registerForm.username;
}
async function issueRegistrationChallenge(method, graphic) {
  info.value='';session.error='';session.errorKey='';sendingCode.value=true;
  try {
    const recipient=method==='phone' ? `+${registerForm.phoneCountryCode || defaultCountryCode.value}${registerForm.phone.replace(/\s/g, '')}` : method==='email' ? registerForm.email : registerForm.username;
    const data=await registrationRequest({action:graphic?'captcha':'send_code',method,recipient});
    // Ignore a response if the account was edited while sending.
    const current=method==='phone' ? `+${registerForm.phoneCountryCode || defaultCountryCode.value}${registerForm.phone.replace(/\s/g, '')}` : method==='email' ? registerForm.email : registerForm.username;
    if(recipient!==current)return;
    if(method==='phone'){phoneCaptcha.value=data.challenge.id;phoneCaptchaImage.value=data.challenge.image||'';registerForm.code='';}
    else if(method==='email'){emailChallenge.value=data.challenge.id;registerForm.emailCode='';}
    else{usernameCaptcha.value=data.challenge.id;usernameCaptchaImage.value=data.challenge.image;registerForm.usernameCode='';}
    if(!graphic)info.value='registration.codeSent';
  }catch(error){session.error='';session.errorKey=registrationErrorKey(error);}finally{sendingCode.value=false;}
}
function requestRegisterCode(){return issueRegistrationChallenge('phone',false);}
function requestEmailCode(){return issueRegistrationChallenge('email',false);}

function submit() {
  syncLoginCredentials();
  loginFormError.value = '';
  session.error = '';
  session.errorKey = '';
  session.errorCode = '';
  if (!/^[1-9][0-9]{0,5}$/.test(loginCountryCode.value)) {
    loginFormError.value = language.t('auth.phoneCountryCode');
    return;
  }
  if (mobile.value.trim().length < 3) {
    loginFormError.value = language.t('registration.identityPlaceholder');
    return;
  }
  if (mode.value === 'password') {
    if (!password.value) {
      loginFormError.value = language.t('auth.passwordPlaceholder');
      loginPasswordInput.value?.focus();
      return;
    }
    session.loginWithPassword(mobile.value, password.value, `+${loginCountryCode.value}`);
    return;
  }
  if (!code.value) {
    loginFormError.value = language.t('auth.localCodePlaceholder');
    return;
  }
  session.loginWithCode(loginPhone(), code.value);
}

function setLoginMode(nextMode) {
  session.error = '';
  session.errorKey = '';
  session.errorCode = '';
  info.value = '';
  mode.value = nextMode;
}

function normalizeUsernameCaptchaInput() {
  registerForm.usernameCode = registerForm.usernameCode.toLowerCase().replace(/[^ac-hjkmnpr-z2-7]/g, '').slice(0, 6);
}

function normalizePhoneCaptchaInput() {
  registerForm.code = registerForm.code.toLowerCase().replace(/[^ac-hjkmnpr-z2-7]/g, '').slice(0, 6);
}

function normalizeRegisterCountryCode() {
  registerForm.phoneCountryCode = registerForm.phoneCountryCode.replace(/\D/g, '').slice(0, 6);
}

function showPhoneCaptcha(){return issueRegistrationChallenge('phone',true);}
function showUsernameCaptcha(){return issueRegistrationChallenge('username',true);}

function getCaptchaClass(status) {
  return {
    'captcha-input-valid': status === 'valid',
    'captcha-input-invalid': status === 'invalid'
  };
}

async function findReferrer(forRegistration){
  const keyword=registerForm.referrer;
  if(!keyword){selectedReferrer.value=null;referrerMissing.value=false;referrerErrorKey.value='';return true;}
  const epoch=++referrerLookupEpoch;
  referrerSearching.value=true;referrerMissing.value=false;referrerErrorKey.value='';
  try{
    const data=await registrationRequest({action:'find_referrer',referrer:keyword});
    if(epoch!==referrerLookupEpoch||keyword!==registerForm.referrer||view.value!=='register')return false;
    const matches=data.matches||[];
    if(!matches.length){selectedReferrer.value=null;referrerMissing.value=true;referrerErrorKey.value='registration.referrerNotFound';return false;}
    const selected=forRegistration&&selectedReferrer.value?matches.find(person=>person.userID===selectedReferrer.value.userID):null;
    if(selected){selectedReferrer.value=selected;return true;}
    if(forRegistration&&selectedReferrer.value){selectedReferrer.value=null;referrerMissing.value=true;referrerErrorKey.value='registration.referrerNotFound';return false;}
    if(matches.length===1){selectedReferrer.value=matches[0];return true;}
    showReferrerChoices(matches,forRegistration);return false;
  }catch(error){if(epoch===referrerLookupEpoch)referrerErrorKey.value=registrationErrorKey(error);return false;}
  finally{referrerSearching.value=false;}
}
function showReferrerChoices(matches,resume){referrerMatches.value=matches;registerAfterReferrerChoice=resume;referrerDialog.value=true;nextTick(()=>referrerDialogElement.value?.querySelector('button')?.focus());}
function closeReferrerDialog(){referrerDialog.value=false;registerAfterReferrerChoice=false;nextTick(()=>referrerInputElement.value?.focus());}
function chooseReferrer(person){const resume=registerAfterReferrerChoice;selectedReferrer.value=person;referrerMissing.value=false;referrerErrorKey.value='';closeReferrerDialog();if(resume)submitRegister();}
function handleReferrerDialogKey(event){
  if(event.key==='Escape'){event.preventDefault();closeReferrerDialog();return;}
  if(event.key!=='Tab')return;
  const buttons=[...referrerDialogElement.value.querySelectorAll('button')];const first=buttons[0],last=buttons[buttons.length-1];
  if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
}

async function submitRegister() {
  if (!canRegisterSubmit.value || registering.value || referrerSearching.value) return;
  session.error='';session.errorKey='';info.value='';registering.value=true;
  const method=registerType.value;
  try {
    if (!await findReferrer(true)) return;
    const recipient=registrationRecipient();
    const challenge_id=method==='phone'?phoneCaptcha.value:method==='email'?emailChallenge.value:usernameCaptcha.value;
    const code=method==='phone'?registerForm.code:method==='email'?registerForm.emailCode:registerForm.usernameCode;
    const data=await registrationRequest({action:'register',method,recipient,challenge_id,code,password:registerForm.password,area_code:`+${registerForm.phoneCountryCode||defaultCountryCode.value}`,referrer:registerForm.referrer,referrer_user_id:selectedReferrer.value?.userID||''});
    registerForm.referrer='';selectedReferrer.value=null;
    if(method==='phone'){loginCountryCode.value=registerForm.phoneCountryCode;}
    mobile.value=method==='phone'?registerForm.phone:recipient;
    password.value='';registerForm.password='';registerForm.confirmPassword='';mode.value='password';view.value='login';info.value='registration.success';
  }catch(error){
    if(error.code==='referrerNotFound'){referrerMissing.value=true;referrerErrorKey.value='registration.referrerNotFound';return;}
    if(error.code==='referrerMultiple'){showReferrerChoices(error.matches,true);return;}
    if(error.code==='referrerTooMany'){referrerErrorKey.value='registration.referrerTooMany';return;}
    session.error='';session.errorKey=registrationErrorKey(error);
    if(method==='username'){usernameCaptcha.value='';usernameCaptchaImage.value='';registerForm.usernameCode='';}
    if(method==='phone'&&!mobileRealVerify.value){phoneCaptcha.value='';phoneCaptchaImage.value='';registerForm.code='';}
  }finally{registering.value=false;}
}

async function submitRegisterPassword() {
  session.error = '';
  session.errorKey = '';
  info.value = '';
  if (!pendingRegistration.mobile || !pendingRegistration.resetCode) {
    session.errorKey = 'registration.expired';
    return;
  }
  if (!registerPasswordForm.password || !registerPasswordForm.confirmPassword) {
    session.errorKey = 'registration.passwordIncomplete';
    return;
  }
  if (registerPasswordForm.password !== registerPasswordForm.confirmPassword) {
    session.errorKey = 'registration.passwordMismatch';
    return;
  }

  settingRegisterPassword.value = true;
  try {
    await session.resetPasswordWithCode({
      mobile: pendingRegistration.mobile,
      resetCode: pendingRegistration.resetCode,
      newPassword: registerPasswordForm.password
    });
    await session.loginWithPassword(pendingRegistration.mobile, registerPasswordForm.password);
    if (!session.error) {
      clearPendingRegistration();
    }
  } catch (error) {
    session.errorKey = 'registration.passwordSaveFailed';
  } finally {
    settingRegisterPassword.value = false;
  }
}

function formatPhoneForApi(countryCode, phone) {
  const digits = String(countryCode || defaultCountryCode.value).replace(/\D/g, '') || defaultCountryCode.value;
  const normalizedCode = `+${digits}`;
  const normalizedPhone = String(phone || '').replace(/\s/g, '');
  if (normalizedCode === '+86') {
    return normalizedPhone;
  }
  return `${normalizedCode} ${normalizedPhone}`;
}

async function loadRegisterConfig() {
  registrationLoading.value=true;
  try {
    const response=await registrationRequest();
    registerMethods.value=response.data.methods;
    mobileRealVerify.value=Boolean(response.data.mobile_real_verify);
  }catch(error){registerMethods.value={phone:false,email:false,username:false};session.error='';session.errorKey=registrationErrorKey(error);}
  finally{registrationLoading.value=false;}
}

async function loadPlatformConfig() { await loadProjectBrand(); }

function handleBrandLogoError() { projectBrand.icon_url = defaultPlatformLogo; }

function clearRegisterPhoneError() {
  registerPhoneError.value = '';
}

function clearPendingRegistration() {
  pendingRegistration.mobile = '';
  pendingRegistration.result = null;
  pendingRegistration.resetCode = '';
  registerPasswordForm.password = '';
  registerPasswordForm.confirmPassword = '';
  sessionStorage.removeItem(pendingRegistrationStorageKey);
}

function setPendingRegistration(phone, result) {
  pendingRegistration.mobile = phone;
  pendingRegistration.result = result;
  pendingRegistration.resetCode = String(result.resetCode || '');
  sessionStorage.setItem(pendingRegistrationStorageKey, JSON.stringify({
    appServer: appConfig.appServer,
    mobile: pendingRegistration.mobile,
    resetCode: pendingRegistration.resetCode,
    result
  }));
}

function restorePendingRegistration() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(pendingRegistrationStorageKey) || 'null');
    if (!pending || pending.appServer !== appConfig.appServer || !pending.mobile || !pending.resetCode) {
      return;
    }
    pendingRegistration.mobile = pending.mobile;
    pendingRegistration.result = pending.result || null;
    pendingRegistration.resetCode = String(pending.resetCode || '');
    registerPasswordForm.password = '';
    registerPasswordForm.confirmPassword = '';
    view.value = 'setPassword';
  } catch (error) {
    sessionStorage.removeItem(pendingRegistrationStorageKey);
  }
}

function selectRegisterMethod(method) {
  referrerLookupEpoch++;closeReferrerDialog();
  registerType.value = method;
  session.error = '';
  session.errorKey = '';
  info.value = '';
  clearRegisterPhoneError();
}

function openRegister() {
  session.error = '';
  session.errorKey = '';
  info.value = '';
  clearRegisterPhoneError();
  clearPendingRegistration();
  registerForm.phone = registerForm.phone || (/^[0-9]+$/.test(mobile.value) ? mobile.value : '');
  registerForm.code = '';
  phoneCaptcha.value = '';
  phoneCaptchaImage.value = '';
  registerType.value = visibleRegisterMethods.value.length === 1 ? visibleRegisterMethods.value[0].key : '';
  view.value = 'register';
  loadRegisterConfig();
}

function backToLogin() {
  referrerLookupEpoch++;closeReferrerDialog();
  session.error = '';
  session.errorKey = '';
  info.value = '';
  clearRegisterPhoneError();
  clearPendingRegistration();
  mode.value = 'password';
  view.value = 'login';
}
</script>

<style scoped>
.referrer-row{display:flex;gap:8px}.referrer-row input{flex:1;min-width:0}.login-form input.referrer-not-found{background:#ffe4e4;border-color:#cf3030;color:#851818}.referrer-field small:empty{display:none}.referrer-overlay{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;background:rgba(0,0,0,.45);padding:16px}.referrer-dialog{width:min(460px,100%);max-height:90vh;overflow:auto;border-radius:12px;border:1px solid var(--line);background:var(--panel);color:var(--text);padding:20px}.referrer-dialog header{display:flex;align-items:center;gap:16px;justify-content:space-between}.referrer-dialog h2{font-size:19px;margin:0}.referrer-dialog header button{border:0;background:transparent;color:inherit;font-size:24px;padding:6px}.referrer-options{display:grid;gap:10px;margin:16px 0;max-height:50vh;overflow:auto}.referrer-options button{display:grid;gap:5px;text-align:left;padding:12px;border:1px solid var(--line);border-radius:8px;background:var(--panel);color:inherit;overflow-wrap:anywhere}.referrer-options button:hover{border-color:var(--brand)}.referrer-options small{color:var(--muted)}
</style>

<style scoped>
.login-shell { width: min(420px, 100%); }
.login-install { margin-bottom: 18px; }
</style>
